import React, { useState, useEffect, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-toastify';
import GenericModal from '../../../components/modal/GenericModal';
import InputText from '../../../components/modalComponents/InputText';
import InputSelect from '../../../components/modalComponents/InputSelect';
import AsyncSelectInput from '../../../components/Select/AsyncSelectInput';
import ModalEnabled from '../../../components/modalComponents/ModalEnabled';
import ConfirmarGuardarNotificarModal from './ConfirmarGuardarNotificarModal';
import ConfirmarEliminarEntrevistaModal from './ConfirmarEliminarEntrevistaModal';
import { useGetRequest } from '../../../hooks/useGetRequest/useGetRequest';
import { usePostRequest } from '../../../hooks/usePostRequest/usePostRequest';
import { responseCode200, mostrarMensajeDeErrorConToast } from '../../../consumers/httpRequiestsUtils';
import { mapDropDownOptionsFromBackendToSelectOptionsNeededForLibrary } from '../../../helper/utils';
import { getTPSedesAPI, getResponsableEmpleadoWithoutReserverdPeopleWordTypeAPI, getDetalleForEntrevistaAPI, guardarNotificarEntrevistaAPI, actualizarEntrevistaAPI, eliminarEntrevistaAPI, omitirEtapaEntrevistaAPI, etapaEntrevistaAptoAPI, etapaEntrevistaNoAptoAPI } from '../../../consumers/backendApisUrls';

const MAX_ENTREVISTAS = 4;

const OPCIONES_MODALIDAD = [
    { value: 'P', label: 'Presencial' },
    { value: 'V', label: 'Virtual' },
];

const ETIQUETA_ESTADO_DETALLE = {
    CONFIGURADO: 'Configurado',
    APTO: 'Apto',
    NO_APTO: 'No Apto',
};

// IdEstadoEntrevista (TP_EntrevistaDetalle) en los que ya se registró un
// resultado con resumen del evaluador (30/09/2026, a pedido de Jody).
const ID_ESTADO_ENTREVISTA_APTO = 63;
const ID_ESTADO_ENTREVISTA_NO_APTO = 64;

// Arma "YYYY-MM-DDTHH:mm:ss" a partir del Date que entrega react-datepicker,
// usando los getters en hora LOCAL (getFullYear/getMonth/... en vez de
// toISOString(), que convierte a UTC). Mismo motivo que conHora() en
// convocatoriaRecap.js: el deserializer de Java interpreta un sufijo 'Z' y
// desplaza la hora al huso horario del servidor.
const formatFechaHoraLocal = (fecha) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${fecha.getFullYear()}-${pad(fecha.getMonth() + 1)}-${pad(fecha.getDate())}`
        + `T${pad(fecha.getHours())}:${pad(fecha.getMinutes())}:${pad(fecha.getSeconds())}`;
};

// Una entrevista ya evaluada (Apto/No apto) no se puede modificar: cambiar al entrevistador
// o la fecha reescribiría un resultado ya registrado. Una guardada y todavía Configurada sí.
const estaEvaluada = (e) => e.idEstadoEntrevista === ID_ESTADO_ENTREVISTA_APTO
    || e.idEstadoEntrevista === ID_ESTADO_ENTREVISTA_NO_APTO
    || e.estado === 'APTO'
    || e.estado === 'NO_APTO';

// Lo que cada tarjeta tenía al cargarse/guardarse, para saber si "Actualizar y notificar" tiene
// algo que enviar.
const snapshotProgramacion = (e) => ({
    entrevistador: e.entrevistador?.value ?? null,
    fechaHora: e.fechaHora ? e.fechaHora.getTime() : null,
    modalidad: e.modalidad?.value ?? null,
    linkReunion: e.linkReunion ?? '',
    sede: e.sede?.value ?? null,
});

const huboCambios = (e) => {
    if (!e.original) return true;
    const actual = snapshotProgramacion(e);
    return Object.keys(actual).some((campo) => actual[campo] !== e.original[campo]);
};

const EntrevistaPostulante = ({ toggleModal, refreshPage, postulante = {}, titulo = 'Configurar entrevista', esGDH = false }) => {
    const [executeGet] = useGetRequest();
    const [executePost] = usePostRequest();

    const [entrevistas, setEntrevistas] = useState([]);
    const [sedeOptions, setSedeOptions] = useState([]);
    const [detalle, setDetalle] = useState(null);
    const [guardandoKey, setGuardandoKey] = useState(null);
    // Key de la tarjeta cuya eliminación (borrado lógico) está en curso.
    const [eliminandoKey, setEliminandoKey] = useState(null);
    const [isOmitiendo, setIsOmitiendo] = useState(false);
    // 'APTO' | 'NOAPTO' | null — cuál de los dos botones de decisión de etapa
    // está en curso (28/09/2026, a pedido de Jody), para deshabilitar ambos
    // mientras se resuelve y mostrar "Calificando..." solo en el que se pulsó.
    const [calificandoEtapa, setCalificandoEtapa] = useState(null);
    // Modal de confirmación previo a "Guardar y notificar" / "Eliminar"
    // (30/09/2026, a pedido de Jody) — mismo patrón que modalConfirmacion en
    // ConvocatoriasDisponibles.jsx (ConfirmarPostulacionModal). El mismo slot
    // sirve para las dos confirmaciones.
    const [modalConfirmacion, setModalConfirmacion] = useState({ isEnable: false });
    const proximoIdRef = useRef(1);

    // Cabecera de solo lectura — ver nota arriba: `detalle` (GetDetalleForEntrevista)
    // prevalece en cuanto llega; mientras tanto se usan los datos de la fila de
    // la grilla como fallback (salvo DNI, que no viene en la grilla).
    const nombreColaborador = detalle?.NombreEmpleado ?? postulante?.nombrePostulante ?? '';
    const dni = detalle?.NumeroDocumento ?? '';
    const cargoActual = detalle?.Cargo ?? postulante?.desCargo ?? '';
    const sedeActual = detalle?.Sede ?? postulante?.desSede ?? '';


    const hayEntrevistaEvaluada = entrevistas.some(e => e.estado === 'APTO' || e.estado === 'NO_APTO');

    // (28/09/2026, a pedido de Jody): "Apto"/"No apto" cierran la etapa
    // Entrevista completa a nivel global (TP_Entrevista) — a diferencia de
    // "Guardar y notificar", que guarda cada entrevista individual en
    // TP_EntrevistaDetalle. Solo se habilitan cuando TODAS las entrevistas ya
    // guardadas (es decir, las que ya existen en TP_EntrevistaDetalle; una
    // tarjeta nueva sin guardar todavía no es un registro real) fueron
    // evaluadas por el Director con Apto o No Apto. Si todavía no hay ninguna
    // entrevista guardada, tampoco se habilitan — para ese caso está "Omitir
    // etapa".
    const entrevistasGuardadas = entrevistas.filter(e => e.guardada);
    const todasEntrevistasEvaluadas = entrevistasGuardadas.length > 0
        && entrevistasGuardadas.every(e => e.estado === 'APTO' || e.estado === 'NO_APTO');
    const tituloDeshabilitadoDecisionEtapa = entrevistasGuardadas.length === 0
        ? 'No hay entrevistas guardadas todavía'
        : (!todasEntrevistasEvaluadas ? 'Debe evaluar (Apto o No apto) todas las entrevistas antes de continuar' : '');


    useEffect(() => {
        if (!postulante?.idPostulante) return;
        let cancelado = false;

        (async () => {
            const detalleResp = await executeGet(getDetalleForEntrevistaAPI(postulante.idPostulante));
            if (cancelado) return;

            if (responseCode200(detalleResp)) {
                setDetalle(detalleResp.data);
                setEntrevistas((detalleResp.data?.Entrevistas ?? []).map(mapEntrevistaCargada));
            } else {
                mostrarMensajeDeErrorConToast(detalleResp);
                toggleModal();
            }
        })();

        return () => { cancelado = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [postulante?.idPostulante]);

    useEffect(() => {
        (async () => {
            const resp = await executeGet(getTPSedesAPI());
            if (responseCode200(resp)) {
                setSedeOptions(mapDropDownOptionsFromBackendToSelectOptionsNeededForLibrary(resp?.data ?? []));
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Repobla el acordeón con las entrevistas ya guardadas (26/09/2026, a pedido
    // de Jody: GetDetalleForEntrevista ahora devuelve `Entrevistas`, ver Java
    // obtenerEntrevistasByPostulante). Arrancan cerradas (isOpen: false) — solo
    // un bloque nuevo arranca abierto, mismo criterio de siempre. La key usa el
    // IdEntrevistaDetalle real, así que no choca con el contador de "nueva-N".
    // Las ya guardadas y sin evaluar se pueden modificar con "Actualizar y notificar"
    // (cambio de entrevistador/fecha, ver ejecutarActualizarEntrevista): el backend
    // actualiza ese mismo detalle, no inserta uno nuevo. Las ya evaluadas (Apto/No apto)
    // quedan bloqueadas. `original` guarda lo que se cargó, para habilitar el botón solo
    // si hubo cambios.
    const mapEntrevistaCargada = (item) => conOriginal({
        key: item.IdEntrevistaDetalle,
        idEntrevistaDetalle: item.IdEntrevistaDetalle,
        entrevistador: item.IdEmpleadoEntrevistador
            ? { value: item.IdEmpleadoEntrevistador, label: item.NombreEntrevistador }
            : null,
        fechaHora: item.FechaHora ? new Date(item.FechaHora) : null,
        modalidad: OPCIONES_MODALIDAD.find(o => o.value === item.TipoModalidad) ?? null,
        linkReunion: item.TipoModalidad === 'V' ? (item.LinkReunion ?? '') : '',
        sede: item.TipoModalidad === 'P' && item.IdSedeReunion
            ? { value: item.IdSedeReunion, label: item.Sede }
            : null,
        estado: item.Estado ?? null,
        idEstadoEntrevista: item.IdEstadoEntrevista ?? null,
        // NroEntrevista real (TP_EntrevistaDetalle): se conserva al eliminar otras
        // entrevistas, así la tarjeta mantiene el número con el que se notificó.
        nroEntrevista: item.NroEntrevista ?? null,
        resumenEvaluador: item.ResumenEvaluador ?? '',
        guardada: true,
        isOpen: false,
    });

    const conOriginal = (entrevista) => ({ ...entrevista, original: snapshotProgramacion(entrevista) });

    const crearEntrevistaVacia = () => ({
        key: `nueva-${proximoIdRef.current++}`,
        idEntrevistaDetalle: null,
        entrevistador: null,
        fechaHora: null,
        modalidad: null,
        linkReunion: '',
        sede: null,
        estado: null,
        idEstadoEntrevista: null,
        nroEntrevista: null,
        resumenEvaluador: '',
        guardada: false,
        original: null,
        isOpen: true,
    });

    // Una entrevista ya evaluada no admite cambios (ver estaEvaluada).
    const actualizarCampoEntrevista = (key, campo, valor) => {
        setEntrevistas(lista => lista.map(e => (e.key === key && !estaEvaluada(e) ? { ...e, [campo]: valor } : e)));
    };

    const handleModalidadChange = (key, opcion) => {
        // Al cambiar de modalidad se limpia el campo que deja de aplicar (Link
        // Reunión / Sede) para no arrastrar un valor que ya no corresponde.
        setEntrevistas(lista => lista.map(e => (e.key === key && !estaEvaluada(e)
            ? { ...e, modalidad: opcion, linkReunion: '', sede: null }
            : e)));
    };

    const toggleEntrevista = (key) => {
        setEntrevistas(lista => lista.map(e => (e.key === key ? { ...e, isOpen: !e.isOpen } : e)));
    };

    const handleNuevaEntrevista = () => {
        if (entrevistas.length >= MAX_ENTREVISTAS) {
            toast.error(`Solo se pueden programar hasta ${MAX_ENTREVISTAS} entrevistas`);
            return;
        }
        setEntrevistas(lista => [...lista, crearEntrevistaVacia()]);
    };

    // Valida los campos de la tarjeta y, si todo está OK, abre el modal de
    // confirmación (30/09/2026, a pedido de Jody) — el guardado real (POST +
    // notificación) recién ocurre si el usuario confirma, en
    // ejecutarGuardarEntrevista.
    const handleGuardarEntrevista = (key) => {
        const entrevista = entrevistas.find(e => e.key === key);
        if (!entrevista) return;

        // Ya evaluada (Apto/No apto): no se modifica. Guardada y sin evaluar: sí, como
        // actualización (cambio de entrevistador/fecha), más abajo.
        if (estaEvaluada(entrevista)) {
            return;
        }
        if (entrevista.guardada && !huboCambios(entrevista)) {
            toast.info('No hay cambios que guardar en esta entrevista.');
            return;
        }

        if (!entrevista.entrevistador) {
            toast.error('Debe seleccionar el entrevistador');
            return;
        }
        if (!entrevista.fechaHora) {
            toast.error('Debe indicar la fecha y hora de la entrevista');
            return;
        }
        if (!entrevista.modalidad) {
            toast.error('Debe indicar la modalidad de la entrevista');
            return;
        }
        if (entrevista.modalidad.value === 'V' && !entrevista.linkReunion) {
            toast.error('Debe indicar el link de la reunión');
            return;
        }
        if (entrevista.modalidad.value === 'P' && !entrevista.sede) {
            toast.error('Debe indicar la sede de la reunión');
            return;
        }

        if (guardandoKey || eliminandoKey) return;

        if (entrevista.guardada) {
            setModalConfirmacion({
                isEnable: true,
                component: ConfirmarGuardarNotificarModal,
                data: {
                    onContinue: () => ejecutarActualizarEntrevista(key),
                    mensaje: 'Se actualizará la entrevista (entrevistador, fecha y lugar) y se notificará al entrevistador y al colaborador. ¿Desea continuar?',
                },
            });
            return;
        }

        setModalConfirmacion({
            isEnable: true,
            component: ConfirmarGuardarNotificarModal,
            data: { onContinue: () => ejecutarGuardarEntrevista(key) },
        });
    };

    // Cambia entrevistador/fecha (y modalidad, link, sede) de una entrevista ya guardada y sin
    // evaluar. POST TrasladosPromociones/ActualizarEntrevista: el backend modifica ese mismo
    // detalle (idEntrevistaDetalle) y avisa al entrevistador y al colaborador.
    const ejecutarActualizarEntrevista = async (key) => {
        const entrevista = entrevistas.find(e => e.key === key);
        if (!entrevista || guardandoKey || !entrevista.idEntrevistaDetalle) return;

        setGuardandoKey(key);
        try {
            const payload = {
                idPostulante: postulante?.idPostulante,
                idEntrevistaDetalle: entrevista.idEntrevistaDetalle,
                idEntrevistador: entrevista.entrevistador?.value,
                fechaHora: formatFechaHoraLocal(entrevista.fechaHora),
                tipoModalidad: entrevista.modalidad.value,
                linkReunion: entrevista.modalidad.value === 'V' ? entrevista.linkReunion : null,
                idSedeReunion: entrevista.modalidad.value === 'P' ? entrevista.sede?.value : null,
            };

            const response = await executePost(actualizarEntrevistaAPI(), payload);
            const ok = response?.status >= 200 && response?.status < 300;
            if (!ok) {
                toast.error(response?.response?.data?.Message ?? response?.response?.data?.message ?? 'No se pudo actualizar la entrevista.');
                return;
            }

            // Lo guardado pasa a ser la nueva base: el botón se vuelve a deshabilitar hasta
            // que haya otro cambio.
            setEntrevistas(lista => lista.map(e => (e.key === key ? conOriginal(e) : e)));
            toast.success('Entrevista actualizada y notificada correctamente.');
            refreshPage && refreshPage();
        } catch (error) {
            console.error('Error actualizando entrevista', error);
            toast.error('No se pudo actualizar la entrevista.');
        } finally {
            setGuardandoKey(null);
        }
    };

    const ejecutarGuardarEntrevista = async (key) => {
        const entrevista = entrevistas.find(e => e.key === key);
        if (!entrevista || guardandoKey || eliminandoKey) return;

        setGuardandoKey(key);
        try {
            const payload = {
                idPostulante: postulante?.idPostulante,
                idEntrevistador: entrevista.entrevistador?.value,
                fechaHora: formatFechaHoraLocal(entrevista.fechaHora),
                tipoModalidad: entrevista.modalidad.value,
                linkReunion: entrevista.modalidad.value === 'V' ? entrevista.linkReunion : null,
                idSedeReunion: entrevista.modalidad.value === 'P' ? entrevista.sede?.value : null,
            };

            const response = await executePost(guardarNotificarEntrevistaAPI(), payload);
            const ok = response?.status >= 200 && response?.status < 300;
            if (!ok) {
                toast.error(response?.response?.data?.Message ?? response?.response?.data?.message ?? 'No se pudo guardar la entrevista.');
                return;
            }


            const idEntrevistaDetalleGuardada = response?.data?.IdEntrevistaDetalle
                ?? response?.data?.idEntrevistaDetalle
                ?? null;
            const nroEntrevistaGuardada = response?.data?.NroEntrevista
                ?? response?.data?.nroEntrevista
                ?? null;
            setEntrevistas(lista => lista.map(e => (e.key === key
                ? conOriginal({
                    ...e,
                    estado: 'CONFIGURADO',
                    guardada: true,
                    idEntrevistaDetalle: idEntrevistaDetalleGuardada ?? e.idEntrevistaDetalle,
                    nroEntrevista: nroEntrevistaGuardada ?? e.nroEntrevista,
                })
                : e)));
            toast.success('Entrevista guardada y notificada correctamente.');
            refreshPage && refreshPage();
        } catch (error) {
            console.error('Error guardando entrevista', error);
            toast.error('No se pudo guardar la entrevista.');
        } finally {
            setGuardandoKey(null);
        }
    };

    // "Eliminar entrevista" (borrado lógico): aplica a cualquier entrevista ya
    // guardada, pendiente (71) o evaluada (63/64). El backend valida dentro de
    // la transacción que la etapa Entrevista siga abierta. Pide confirmación
    // antes de llamar al endpoint (mismo patrón que Guardar y notificar); si
    // está evaluada, el mensaje avisa que deja de contar para la etapa.
    const handleEliminarEntrevista = (key) => {
        const entrevista = entrevistas.find(e => e.key === key);
        if (!entrevista) return;

        if (!entrevista.guardada || !entrevista.idEntrevistaDetalle) return;
        if (eliminandoKey || guardandoKey || isOmitiendo || calificandoEtapa) return;

        const resultado = entrevista.idEstadoEntrevista === ID_ESTADO_ENTREVISTA_APTO || entrevista.estado === 'APTO'
            ? 'Apto'
            : 'No apto';
        setModalConfirmacion({
            isEnable: true,
            component: ConfirmarEliminarEntrevistaModal,
            data: {
                onContinue: () => ejecutarEliminarEntrevista(key),
                ...(estaEvaluada(entrevista) && {
                    mensaje: `Esta entrevista ya fue evaluada como ${resultado}. Si la elimina, dejará de contar para la decisión de la etapa. No se enviará ningún correo. ¿Desea continuar?`,
                }),
            },
        });
    };

    const ejecutarEliminarEntrevista = async (key) => {
        const entrevista = entrevistas.find(e => e.key === key);
        if (!entrevista || !entrevista.idEntrevistaDetalle || eliminandoKey || guardandoKey) return;

        setEliminandoKey(key);
        try {
            const payload = {
                idPostulante: postulante?.idPostulante,
                idEntrevistaDetalle: entrevista.idEntrevistaDetalle,
            };

            const response = await executePost(eliminarEntrevistaAPI(), payload);
            const ok = response?.status >= 200 && response?.status < 300;
            if (!ok) {
                toast.error(response?.response?.data?.Message ?? response?.response?.data?.message ?? 'No se pudo eliminar la entrevista.');
                return;
            }

            // La entrevista eliminada deja de mostrarse; las demás conservan su
            // NroEntrevista original (no se renumeran).
            setEntrevistas(lista => lista.filter(e => e.key !== key));
            toast.success('Entrevista eliminada correctamente.');
            refreshPage && refreshPage();
        } catch (error) {
            console.error('Error eliminando entrevista', error);
            toast.error('No se pudo eliminar la entrevista.');
        } finally {
            setEliminandoKey(null);
        }
    };

    
    const handleOmitirEtapa = async () => {
        if (isOmitiendo || guardandoKey || eliminandoKey) return;
        if (hayEntrevistaEvaluada) {
            // Mismo mensaje/regla que valida el Java (existeEntrevistaEvaluada) —
            // esto es solo para no hacer el viaje al backend cuando ya se sabe la
            // respuesta; la validación real sigue viviendo del lado del servidor.
            toast.error('No puede omitir la etapa porque ya existen Entrevistas evaluadas');
            return;
        }
        setIsOmitiendo(true);
        try {
            const payload = {
                idPostulante: postulante?.idPostulante,
                // El DTO del .NET (OmitirEtapaRequestDto.IdEntrevista) es un int no
                // nulleable, así que hay que mandar algo — el Java ya no lo usa
                // (ver nota arriba), así que el valor es irrelevante.
                idEntrevista: 0,
            };

            const response = await executePost(omitirEtapaEntrevistaAPI(), payload);
            const ok = response?.status >= 200 && response?.status < 300;
            if (!ok) {
                toast.error(response?.response?.data?.Message ?? response?.response?.data?.message ?? 'No se pudo omitir la etapa.');
                return;
            }

            toast.success('La etapa entrevista ha sido omitida satisfactoriamente');
            if (refreshPage) await refreshPage();
            toggleModal();
        } catch (error) {
            console.error('Error omitiendo etapa de entrevista', error);
            toast.error('No se pudo omitir la etapa.');
        } finally {
            setIsOmitiendo(false);
        }
    };

    // (28/09/2026, a pedido de Jody; conectado 30/09/2026): decisión final de
    // GDH sobre TODA la etapa Entrevista (TP_Entrevista) — a diferencia de
    // "Guardar y notificar"/"Completar entrevista", que actúan por cada
    // entrevista individual. Mismo patrón que handleDecisionGDH en
    // FeedbackDireccion.jsx: un solo handler parametrizado por esApto, llamado
    // por los dos botones. idEntrevista sale de `detalle` (GetDetalleForEntrevista
    // ya lo trae a nivel de cabecera), no de las tarjetas del acordeón.
    const handleCalificarEtapaEntrevista = async (esApto) => {
        if (calificandoEtapa || isOmitiendo || guardandoKey || eliminandoKey) return;
        if (!todasEntrevistasEvaluadas) {
            // El botón ya está disabled en este caso — esto es solo para no
            // depender únicamente del disabled del DOM.
            toast.error(tituloDeshabilitadoDecisionEtapa);
            return;
        }

        setCalificandoEtapa(esApto ? 'APTO' : 'NOAPTO');
        try {
            const payload = {
                idPostulante: postulante?.idPostulante,
                idEntrevista: detalle?.IdEntrevista,
            };

            const api = esApto ? etapaEntrevistaAptoAPI() : etapaEntrevistaNoAptoAPI();
            const response = await executePost(api, payload);
            const ok = response?.status >= 200 && response?.status < 300;
            if (!ok) {
                toast.error(response?.response?.data?.Message ?? response?.response?.data?.message ?? 'No se pudo calificar la etapa.');
                return;
            }

            toast.success(esApto
                ? 'La etapa Entrevista fue calificada como Apto'
                : 'La etapa Entrevista fue calificada como No apto');
            if (refreshPage) await refreshPage();
            toggleModal();
        } catch (error) {
            console.error('Error calificando la etapa de entrevista', error);
            toast.error('No se pudo calificar la etapa.');
        } finally {
            setCalificandoEtapa(null);
        }
    };

    const handleAptoEtapaEntrevista = () => handleCalificarEtapaEntrevista(true);
    const handleNoAptoEtapaEntrevista = () => handleCalificarEtapaEntrevista(false);

    return (
        <GenericModal showModal={toggleModal} titulo={titulo} width="960px">
            <ModalEnabled modalEnabled={modalConfirmacion} setModalEnabled={setModalConfirmacion} />
            <div className="modal-body pt-3">
                <div className="row">
                    <div className="col-sm-3"><InputText label="Colaborador" name="colaborador" value={nombreColaborador} direction="column" isDisabled /></div>
                    <div className="col-sm-3"><InputText label="DNI" name="dni" value={dni} direction="column" isDisabled /></div>
                    <div className="col-sm-3"><InputText label="Cargo actual" name="cargoActual" value={cargoActual} direction="column" isDisabled /></div>
                    <div className="col-sm-3"><InputText label="Sede actual" name="sedeActual" value={sedeActual} direction="column" isDisabled /></div>
                </div>

                <hr />

                <div className="d-flex justify-content-center mb-3">
                    <button type="button" className="btn generic-button-buscador" onClick={handleNuevaEntrevista}
                        disabled={entrevistas.length >= MAX_ENTREVISTAS}
                        title={entrevistas.length >= MAX_ENTREVISTAS ? `Máximo ${MAX_ENTREVISTAS} entrevistas` : ''}>
                        Nueva entrevista
                    </button>
                </div>

                {entrevistas.length === 0 &&
                    <p className="text-center text-muted">Todavía no se configuró ninguna entrevista.</p>
                }

                {entrevistas.map((entrevista, index) => (
                    <div className="card mb-2" key={entrevista.key}>
                        <div className="card-header hand-pointer d-flex justify-content-between align-items-center"
                            onClick={() => toggleEntrevista(entrevista.key)}>
                            <span className="font-gothan-medium">
                                {`Entrevista #${entrevista.nroEntrevista ?? index + 1}`}
                                {entrevista.estado ? ` - ${ETIQUETA_ESTADO_DETALLE[entrevista.estado] ?? entrevista.estado}` : ''}
                            </span>
                            <FontAwesomeIcon icon={entrevista.isOpen ? 'chevron-up' : 'chevron-down'} />
                        </div>

                        {entrevista.isOpen &&
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-sm-6">
                                        <label className="modal-label d-flex">Entrevistador</label>
                                        <AsyncSelectInput
                                            api={getResponsableEmpleadoWithoutReserverdPeopleWordTypeAPI}
                                            handleSelectInputChange={valor => actualizarCampoEntrevista(entrevista.key, 'entrevistador', valor)}
                                            defaultValue={entrevista.entrevistador}
                                            placeholder="Búsqueda de colaboradores" />
                                    </div>
                                    <div className="col-sm-6">
                                        <label className="modal-label d-flex">Fecha / Hora</label>
                                        <DatePicker className="form-control" name="fechaHora"
                                            selected={entrevista.fechaHora}
                                            onChange={fecha => actualizarCampoEntrevista(entrevista.key, 'fechaHora', fecha)}
                                            disabled={estaEvaluada(entrevista)}
                                            showTimeSelect
                                            timeIntervals={15}
                                            timeCaption="Hora"
                                            dateFormat="dd/MM/yyyy HH:mm"
                                            placeholderText="dd/mm/aaaa --:--" />
                                    </div>
                                </div>
                                <div className="row mt-2">
                                    <div className="col-sm-6">
                                        <InputSelect label="Modalidad" name="modalidad" direction="column"
                                            options={OPCIONES_MODALIDAD}
                                            value={entrevista.modalidad}
                                            onChange={e => handleModalidadChange(entrevista.key, e.target.value)} />
                                    </div>
                                    <div className="col-sm-6">
                                        {entrevista.modalidad?.value === 'V' &&
                                            <>
                                                <label className="modal-label d-flex orange_innova">Link reunión</label>
                                                <input type="text" className="form-control" placeholder="Ingresar link de la reunión"
                                                    value={entrevista.linkReunion}
                                                    onChange={e => actualizarCampoEntrevista(entrevista.key, 'linkReunion', e.target.value)} />
                                            </>
                                        }
                                        {entrevista.modalidad?.value === 'P' &&
                                            <InputSelect label="Sede" name="sede" direction="column"
                                                options={sedeOptions}
                                                value={entrevista.sede}
                                                onChange={e => actualizarCampoEntrevista(entrevista.key, 'sede', e.target.value)}
                                                placeholder="Seleccionar sede" />
                                        }
                                    </div>
                                </div>

                                {(entrevista.idEstadoEntrevista === ID_ESTADO_ENTREVISTA_APTO
                                    || entrevista.idEstadoEntrevista === ID_ESTADO_ENTREVISTA_NO_APTO) &&
                                    <div className="row mt-2">
                                        <div className="col-sm-12">
                                            <label className="modal-label d-flex">Resumen evaluador</label>
                                            <textarea className="form-control" rows={3} disabled
                                                value={entrevista.resumenEvaluador ?? ''} onChange={() => {}} />
                                        </div>
                                    </div>
                                }

                                <div className="d-flex justify-content-center align-items-center mt-3">
                                    {/* Nueva: "Guardar y notificar". Ya guardada y sin evaluar: "Actualizar y
                                        notificar", habilitado solo si cambió algo. Ya evaluada: bloqueada. */}
                                    <button type="button" className="btn generic-button-buscador"
                                        disabled={!!guardandoKey || !!eliminandoKey || estaEvaluada(entrevista)
                                            || (!!entrevista.guardada && !huboCambios(entrevista))}
                                        title={estaEvaluada(entrevista)
                                            ? 'Esta entrevista ya fue evaluada y no se puede modificar'
                                            : (entrevista.guardada && !huboCambios(entrevista)
                                                ? 'Modifique el entrevistador, la fecha u otro dato para poder actualizar'
                                                : '')}
                                        onClick={() => handleGuardarEntrevista(entrevista.key)}>
                                        {guardandoKey === entrevista.key
                                            ? 'Guardando...'
                                            : (estaEvaluada(entrevista)
                                                ? 'Evaluada'
                                                : (entrevista.guardada ? 'Actualizar y notificar' : 'Guardar y notificar'))}
                                    </button>
                                    {/* Eliminar (borrado lógico): cualquier entrevista guardada, pendiente
                                        o evaluada, solo para GDH. Que la etapa siga abierta lo valida el
                                        backend dentro de la transacción. */}
                                    {esGDH && entrevista.guardada &&
                                        <button type="button" className="btn btn-danger ml-3"
                                            disabled={!!eliminandoKey || !!guardandoKey || isOmitiendo || !!calificandoEtapa}
                                            onClick={() => handleEliminarEntrevista(entrevista.key)}>
                                            {eliminandoKey === entrevista.key ? 'Eliminando...' : 'Eliminar'}
                                        </button>
                                    }
                                </div>
                            </div>
                        }
                    </div>
                ))}

                <div className="d-flex justify-content-center mt-4">
                    <button type="button" className="btn btn-success mr-3"
                        disabled={!todasEntrevistasEvaluadas || !!calificandoEtapa || isOmitiendo || !!guardandoKey || !!eliminandoKey}
                        title={tituloDeshabilitadoDecisionEtapa}
                        onClick={handleAptoEtapaEntrevista}>
                        {calificandoEtapa === 'APTO' ? 'Calificando...' : 'Apto'}
                    </button>
                    <button type="button" className="btn btn-danger mr-3"
                        disabled={!todasEntrevistasEvaluadas || !!calificandoEtapa || isOmitiendo || !!guardandoKey || !!eliminandoKey}
                        title={tituloDeshabilitadoDecisionEtapa}
                        onClick={handleNoAptoEtapaEntrevista}>
                        {calificandoEtapa === 'NOAPTO' ? 'Calificando...' : 'No apto'}
                    </button>
                    <button type="button" className="btn generic-button-buscador"
                        disabled={isOmitiendo || !!guardandoKey || !!calificandoEtapa || !!eliminandoKey || hayEntrevistaEvaluada}
                        title={hayEntrevistaEvaluada ? 'No puede omitir la etapa porque ya existen Entrevistas evaluadas' : ''}
                        onClick={handleOmitirEtapa}>
                        {isOmitiendo ? 'Omitiendo...' : 'Omitir etapa'}
                    </button>
                </div>
            </div>
        </GenericModal>
    )
}

export default EntrevistaPostulante
